import { quoteThemeColorStyles } from "../quote-theme/quote-theme-css-vars";
import { iDestinationSummaryBlockData } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { MapPin, Clock, Zap, Wifi, DollarSign, Languages } from "lucide-react";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import { usePrimaryColorStyles } from "../contexts/use-primary-color-styles";

interface DestinationSummarySectionProps {
  data: iDestinationSummaryBlockData;
  primaryColor?: string;
}

export function DestinationSummarySection({
  data,
  primaryColor: primaryColorProp,
}: DestinationSummarySectionProps) {
  const { primaryColor: primaryColorFromContext } = useHolidayQuoteBrand();
  const { containerStyle, textIconStyle } = usePrimaryColorStyles();
  // Use prop if provided, otherwise use context (for backward compatibility)
  const primaryColor = primaryColorProp || primaryColorFromContext;
  const place = data.place;

  const InfoItem = ({ 
    icon: Icon, 
    label, 
    value, 
    className = "" 
  }: { 
    icon: any; 
    label: string; 
    value: string; 
    className?: string;
  }) => (
    <div className={`flex items-center space-x-3 ${className}`}>
      <div 
        className="p-2 rounded-lg"
        style={containerStyle}
      >
        <Icon 
          className="h-4 w-4" 
          style={textIconStyle}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        <div className="text-sm text-gray-600">{value}</div>
      </div>
    </div>
  );

  return (
    <div className="w-full rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="flex items-center space-x-3">
          <MapPin 
            className="h-5 w-5" 
            style={quoteThemeColorStyles.primary}
          />
          <div>
            <h3 className="text-xl font-semibold leading-none tracking-tight">
              {place?.name || "Destination"}
            </h3>
            {place?.formatted_address && (
              <p className="text-sm text-muted-foreground mt-1">
                {place.formatted_address}
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="p-6 pt-0 space-y-6">
        {/* City Highlights */}
        {data.cityHighlights && (
          <div>
            <h4 className="font-semibold mb-3" style={quoteThemeColorStyles.primary}>
              City Highlights
            </h4>
            <p className="text-gray-700 leading-relaxed">
              {data.cityHighlights}
            </p>
          </div>
        )}

        {/* Essential Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.timezone && (
            <InfoItem
              icon={Clock}
              label="Timezone"
              value={data.timezone}
            />
          )}
          
          {data.powerSocket && (
            <InfoItem
              icon={Zap}
              label="Power Socket"
              value={data.powerSocket}
            />
          )}
          
          {data.internetSpeed && (
            <InfoItem
              icon={Wifi}
              label="Internet"
              value={data.internetSpeed}
            />
          )}
          
          {data.currency && (
            <InfoItem
              icon={DollarSign}
              label="Currency"
              value={data.currency}
            />
          )}
          
          {data.languages && (
            <InfoItem
              icon={Languages}
              label="Languages"
              value={data.languages}
              className="md:col-span-2"
            />
          )}
        </div>

        {/* Gallery */}
        {data.gallery && data.gallery.length > 0 && (
          <div>
            <h4 className="font-semibold mb-3" style={quoteThemeColorStyles.primary}>
              Gallery
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {data.gallery.map((image, index) => (
                <div key={index} className="aspect-video rounded-lg overflow-hidden">
                  <img
                    src={image}
                    alt={`${place?.name || 'Destination'} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 